// Centralized error handling and logging service

export interface ErrorContext {
  userId?: string;
  component?: string;
  action?: string;
  metadata?: Record<string, unknown>;
}

export interface LoggedError {
  id: string;
  code: string;
  message: string;
  stack?: string;
  context: ErrorContext;
  timestamp: string;
  severity: "low" | "medium" | "high" | "critical";
}

class ErrorService {
  private errors: LoggedError[] = [];
  private maxErrors = 100; // Keep last 100 errors in memory

  /**
   * Log an error with context
   */
  logError(
    error: Error | string,
    context: ErrorContext = {},
    severity: LoggedError["severity"] = "medium"
  ): LoggedError {
    const errorId = this.generateErrorId();
    const errorMessage = typeof error === "string" ? error : error.message;
    const stack = typeof error === "string" ? undefined : error.stack;

    const loggedError: LoggedError = {
      id: errorId,
      code: this.generateErrorCode(errorMessage, context),
      message: errorMessage,
      stack,
      context,
      timestamp: new Date().toISOString(),
      severity,
    };

    // Add to memory store
    this.errors.unshift(loggedError);
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Log to console in development
    if (process.env.NODE_ENV === "development") {
      console.group(`🚨 Error ${severity.toUpperCase()}: ${loggedError.code}`);
      console.error("Message:", errorMessage);
      console.error("Context:", context);
      if (stack) console.error("Stack:", stack);
      console.groupEnd();
    }

    // Report medium+ severity errors to Sentry in production
    if (process.env.NODE_ENV === "production" && (severity === "critical" || severity === "high")) {
      this.reportToExternalService(loggedError);
    }

    return loggedError;
  }

  /**
   * Create app-specific error
   */
  createAppError(
    code: string,
    message: string,
    context: ErrorContext = {}
  ): AppError {
    const errorId = this.generateErrorId();
    
    return {
      code,
      message,
      details: context.metadata,
      timestamp: new Date().toISOString(),
    };
  }

  /**
   * Get recent errors
   */
  getRecentErrors(limit = 10): LoggedError[] {
    return this.errors.slice(0, limit);
  }

  /**
   * Get errors by severity
   */
  getErrorsBySeverity(severity: LoggedError["severity"]): LoggedError[] {
    return this.errors.filter(error => error.severity === severity);
  }

  /**
   * Clear all errors
   */
  clearErrors(): void {
    this.errors = [];
  }

  /**
   * Handle React error boundary errors
   */
  handleReactError(error: Error, errorInfo: { componentStack: string }): LoggedError {
    return this.logError(error, {
      component: "ErrorBoundary",
      action: "component_error",
      metadata: {
        componentStack: errorInfo.componentStack,
      },
    }, "high");
  }

  /**
   * Handle async operation errors
   */
  handleAsyncError(
    error: Error,
    operation: string,
    context: ErrorContext = {}
  ): LoggedError {
    return this.logError(error, {
      ...context,
      action: operation,
    }, "medium");
  }

  /**
   * Handle API errors
   */
  handleApiError(
    error: Error,
    endpoint: string,
    context: ErrorContext = {}
  ): LoggedError {
    const isNetworkError = !navigator.onLine;
    const severity = isNetworkError ? "low" : "medium";

    return this.logError(error, {
      ...context,
      action: "api_request",
      metadata: {
        endpoint,
        isNetworkError,
        ...context.metadata,
      },
    }, severity);
  }

  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateErrorCode(message: string, context: ErrorContext): string {
    const component = context.component || "UNKNOWN";
    const action = context.action || "OPERATION";
    
    // Create a simple hash from the message
    const messageHash = message
      .substring(0, 10)
      .replace(/[^a-zA-Z0-9]/g, "")
      .toUpperCase() || "ERROR";

    return `${component}_${action}_${messageHash}`;
  }

  private async reportToExternalService(error: LoggedError): Promise<void> {
    try {
      const Sentry = await import("@sentry/react");
      Sentry.captureException(new Error(error.message), {
        tags: {
          errorCode: error.code,
          severity: error.severity,
          component: error.context.component,
        },
        extra: {
          errorId: error.id,
          context: error.context,
          stack: error.stack,
        },
      });
    } catch (reportError) {
      console.error("Failed to report error to Sentry:", reportError);
    }
  }
}

// Singleton instance
export const errorService = new ErrorService();

// Type definition for app errors
export interface AppError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  timestamp: string;
}

// Utility function for error handling in React components
export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => R,
  context: ErrorContext = {}
) => {
  return (...args: T): R => {
    try {
      return fn(...args);
    } catch (error) {
      errorService.logError(
        error as Error,
        context,
        "medium"
      );
      throw error; // Re-throw so React error boundaries can catch it
    }
  };
};

// Async error handling wrapper
export const withAsyncErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  operation: string,
  context: ErrorContext = {}
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (error) {
      errorService.handleAsyncError(
        error as Error,
        operation,
        context
      );
      throw error; // Re-throw for upstream handling
    }
  };
};